import { useCallback, useContext } from 'react';
import { toast } from 'react-toastify';
import AuthContext from '../context/authContext';
import useHttp from './useHttp';
import { CURRENT_FRONTEND } from '../util/frontendMigration';

// API hook for the legacy vs. new management-frontend migration: usage
// tracking (pageviews/heartbeats/switches) and the statistics view. The
// one-time announcement popup and the recurring "why still the old tool?"
// prompt are gone — this app is being switched off, so the only message left
// is the shutdown notice, which needs no server state.
const useFrontendUsage = () => {
  const authCtx = useContext(AuthContext);
  const { sendRequest } = useHttp();

  // Background telemetry (pageview/heartbeat). No toast — tracking must never
  // bother the user; callers catch and ignore failures.
  const trackEvent = useCallback(
    async ({ eventType, path, viewTitle }) => {
      return await sendRequest({
        url: '/api/v1/frontendUsage/events',
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body: { frontend: CURRENT_FRONTEND, eventType, path, viewTitle: viewTitle || undefined },
        noAutoStore: true,
      });
    },
    [authCtx, sendRequest]
  );

  // Records an active switch to the new frontend (before navigating there).
  const trackSwitchToNewFrontend = useCallback(
    async ({ path, viewTitle, origin }) => {
      return await sendRequest({
        url: '/api/v1/frontendUsage/switch',
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body: {
          fromFrontend: CURRENT_FRONTEND,
          toFrontend: 'new',
          path,
          viewTitle: viewTitle || undefined,
          origin,
        },
        noAutoStore: true,
      });
    },
    [authCtx, sendRequest]
  );

  // Aggregated migration statistics (right frontendUsage:statistics:read).
  const getStatistics = useCallback(
    async ({ from, to } = {}) => {
      try {
        return await sendRequest({
          url: '/api/v1/frontendUsage/statistics',
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
          params: { from, to },
        });
      } catch (err) {
        toast.error('Fehler beim Laden der Frontend-Migrations-Statistik');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  return {
    trackEvent,
    trackSwitchToNewFrontend,
    getStatistics,
  };
};

export default useFrontendUsage;
